/**
 * 管理者の同席（2026-10-14 柴田決定・方式B「同席コード」）。作業者の側。
 *
 * 作業者がマニュアル（と、本文を含むレビュー画面）を開くときは、管理者の画面に出る
 * 6桁の同席コードを入力する。入力が通ると functions/presence.mjs が同席の記録を作り、
 * 閲覧プロキシ（viewer/authz.mjs）が配信を許す。**判定はプロキシ側が正**で、この部品は
 * 入力の窓口と、開いている間の更新を受け持つだけ。画面を素通りしても本文は出ない。
 *
 * 有効期間は「閉じるまで」（柴田決定）。開いている間 PING_MS ごとに更新し、閉じると
 * 終了を送る。終了が届かなくても（タブごと閉じた等）、更新が止まれば期限切れになる。
 *
 * 作業者以外（管理者・料理長・上長）はそのまま中身を出す。
 */
import { useCallback, useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { useParams } from "react-router-dom";
import { getFunctions, httpsCallable } from "firebase/functions";
import { app } from "../firebase";
import { useAuth } from "../auth";
import { useI18n } from "../lib/i18n";

const functions = getFunctions(app, "asia-northeast1");
const startPresence = httpsCallable<
  { dishId: string; code: string },
  { ok: boolean; required?: boolean; reason?: string; adminName?: string }
>(functions, "startPresence");
const pingPresence = httpsCallable<{ dishId: string }, { active: boolean; adminName?: string | null }>(
  functions,
  "pingPresence",
);
const endPresence = httpsCallable<{ dishId: string }, { ok: boolean }>(functions, "endPresence");

/** 更新の間隔。期限（90秒）の内側で、背景タブの間引き（1分に1回）でも切れない値 */
const PING_MS = 30 * 1000;

export function PresenceGate({ children }: { children: ReactNode }) {
  const { dishId } = useParams<{ dishId: string }>();
  const { profile } = useAuth();
  if (profile?.role !== "worker" || !dishId) return <>{children}</>;
  return (
    <WorkerGate key={dishId} dishId={dishId}>
      {children}
    </WorkerGate>
  );
}

type State = { kind: "checking" } | { kind: "locked"; note?: string } | { kind: "open"; adminName: string | null };

function WorkerGate({ dishId, children }: { dishId: string; children: ReactNode }) {
  const { t } = useI18n();
  const [state, setState] = useState<State>({ kind: "checking" });
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const open = state.kind === "open";
  const openRef = useRef(false);
  openRef.current = open;

  const ping = useCallback(async () => {
    try {
      const r = await pingPresence({ dishId });
      if (r.data.active) {
        setState({ kind: "open", adminName: r.data.adminName ?? null });
      } else {
        setState(
          openRef.current
            ? { kind: "locked", note: t("同席が終わりました。続けて見るには、管理者に新しいコードを出してもらってください。") }
            : { kind: "locked" },
        );
      }
    } catch {
      // 通信の失敗だけでは閉じない（次の更新で判断する）。開く前なら入力に進める
      if (!openRef.current) setState({ kind: "locked" });
    }
  }, [dishId, t]);

  // 開いた直後: 再読み込みなど、期限内の同席が残っていれば続ける
  useEffect(() => {
    void ping();
  }, [ping]);

  // 開いている間の更新。画面に戻ったときもすぐ確かめる（端末が眠っていた等）
  useEffect(() => {
    if (!open) return;
    const timer = window.setInterval(() => void ping(), PING_MS);
    const onVisible = () => {
      if (document.visibilityState === "visible") void ping();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [open, ping]);

  // 閉じたら終える。画面の移動（アンマウント）とタブを閉じる（pagehide）の両方で送る
  useEffect(() => {
    const end = () => {
      if (openRef.current) void endPresence({ dishId }).catch(() => undefined);
    };
    window.addEventListener("pagehide", end);
    return () => {
      window.removeEventListener("pagehide", end);
      end();
    };
  }, [dishId]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const r = await startPresence({ dishId, code });
      if (r.data.ok) {
        setCode("");
        setState({ kind: "open", adminName: r.data.adminName ?? null });
      } else {
        setError(
          r.data.reason === "expired"
            ? t("このコードは期限が切れています。管理者の画面に出ている新しいコードを入力してください。")
            : r.data.reason === "used"
              ? t("このコードは使用済みです。管理者の画面に出ている新しいコードを入力してください。")
              : t("コードが違います。管理者の画面に出ている6桁の数字を入力してください。"),
        );
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  if (state.kind === "checking") {
    return (
      <div className="page">
        <p className="muted">{t("読み込み中…")}</p>
      </div>
    );
  }

  if (state.kind === "open") {
    return (
      <div className="presence-open">
        <div className="presence-bar">
          {state.adminName
            ? t("{name} さんの同席で閲覧しています", { name: state.adminName })
            : t("管理者の同席で閲覧しています")}
        </div>
        {children}
      </div>
    );
  }

  return (
    <div className="center">
      <form className="card narrow presence-gate" onSubmit={onSubmit}>
        <h1>{t("管理者の同席が必要です")}</h1>
        {state.note && <p className="muted">{state.note}</p>}
        <p>{t("マニュアルは、管理者が同席しているときに見られます。管理者の画面に表示された6桁の同席コードを入力してください。")}</p>
        <label htmlFor="presence-code">{t("同席コード")}</label>
        <input
          id="presence-code"
          className="presence-code-input"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={9}
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder="123456"
          autoFocus
        />
        {error && <p className="error">{error}</p>}
        <button type="submit" disabled={busy || code.trim().length < 6}>
          {busy ? t("処理中…") : t("開く")}
        </button>
      </form>
    </div>
  );
}
